import React, { useState, useEffect, useMemo } from 'react'
import i18n from 'live/services/i18n'
import * as S from './styled'
import TriStateCheckbox from 'live/components/ui/TriStateCheckbox'
import ShowMoreFooter, { ShowMoreFooterProps } from '../ShowMoreFooter/ShowMoreFooter'

export interface TableData {
    id: number
    [field: string]: any
}

export type TableColumns<T = any> = {
    label: string | React.ReactNode
    dataIndex: string
    width?: string
    onClick?: (rowId: string) => void
    render?: (text: string | number, row: T, index: number) => React.ReactNode
}[]

interface GenericTable extends Omit<ShowMoreFooterProps, 'empty'> {
    data: TableData[]
    columns: TableColumns
    selectedIds?: Set<number>
    multiSelect?: boolean
    onClickRow?: (value: any, index: number) => void
    onSelectRow?: (ids: Set<number | string>) => void
    filterProps?: FilterProps
}

const GenericTable: React.FC<GenericTable> = ({
    data,
    filterProps,
    columns,
    multiSelect = false,
    onClickRow,
    onSelectRow,
    selectedIds,
    emptyMessage,
    endOfPages = true,
    endOfPagesMessage,
    loading = false,
    fetchNextPage
}) => {
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
    const indeterminate = selectedRows.size > 0 && selectedRows.size < data.length
    const allSelected = selectedRows.size === data.length

    const filteredData: TableData[] = useMemo(() => {
        let filteredData = data
        if (filterProps?.term.length) {
            filteredData = data.filter(item =>
                String(item[filterProps.column])
                    .toLowerCase()
                    .includes(filterProps.term)
            )
        }
        return filteredData
    }, [data, filterProps])

    const changeSelectedRows = {
        selectAll: () => {
            setSelectedRows(new Set(data.map(item => item.id)))
        },
        toggle: (id: number) => {
            let newSelectedRows = new Set(selectedRows)

            if (!selectedRows.has(id)) {
                newSelectedRows.add(id)
            } else {
                newSelectedRows.delete(id)
            }

            setSelectedRows(newSelectedRows)
        },
        clear: () => setSelectedRows(new Set())
    }


    useEffect(() => {
        changeSelectedRows.clear()
        if (selectedIds) {
            setSelectedRows(selectedIds)
        }
    }, [multiSelect])

    useEffect(() => {
        if (onSelectRow) {
            onSelectRow(selectedRows)
        }
    }, [selectedRows])

    return (
        <>
            <S.Table>
                <thead>
                    <tr className="table-row">
                        {multiSelect && (
                            <th key="check-all">
                                <TriStateCheckbox
                                    indeterminate={indeterminate}
                                    checked={allSelected}
                                    onChange={() => {
                                        if (allSelected) {
                                            changeSelectedRows.clear()
                                        } else {
                                            changeSelectedRows.selectAll()
                                        }
                                    }}
                                />
                            </th>
                        )}
                        {columns.map((column, index) => (
                            <th key={index} style={{ width: column?.width }}>
                                {column.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {filteredData.map((row, rowIndex) => {
                        return (
                            <tr
                                className="table-row"
                                key={rowIndex}
                                onClick={e => {
                                    if (onClickRow) {
                                        //should retur row, instead of row.id
                                        return onClickRow(row.id, rowIndex)
                                    }
                                }}
                            >
                                {multiSelect && (
                                    <td key="multi-select">
                                        <input
                                            checked={selectedRows.has(row.id)}
                                            className="browser-default-input-style"
                                            type="checkbox"
                                            onClick={e => {
                                                e.stopPropagation()
                                                changeSelectedRows.toggle(row.id)
                                            }}
                                        />
                                    </td>
                                )}
                                {columns.map((column, columnIndex) => {
                                    return (
                                        <Cell
                                            key={`${rowIndex}-${columnIndex}`}
                                            rowValues={row}
                                            index={rowIndex}
                                            value={row[column.dataIndex]}
                                            render={column.render}
                                        />
                                    )
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </S.Table>
            {!filteredData.length && (
                <ShowMoreFooter
                    endOfPages={endOfPages}
                    loading={!!loading}
                    fetchNextPage={fetchNextPage}
                    empty={!filteredData.length}
                    endOfPagesMessage={endOfPagesMessage}
                    emptyMessage={emptyMessage || i18n('No data')}
                />
            )}
        </>
    )
}

const Cell: React.FC<{
    rowValues: TableData
    value: string | number
    index: number
    render?: (text: string | number, row: TableData, index: number) => React.ReactNode
}> = ({ rowValues, value, index, render }) => {
    if (render) {
        return <td>{render(value, rowValues, index)}</td>
    }
    return <td>{value}</td>
}

export default React.memo(GenericTable)
